import { defineStore } from 'pinia';
import { ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  FerryMultipleVehiclesData,
  FerryVehiclesRouteData,
  VehiclesLimiation,
} from '@apps/main/new-desktop/modules/ferryMultipleVehiclesModal';
import { usePaxStore, useTripStore } from '@apps/main/new-desktop/core/stores';
import type { FerryIngredient } from '@apps/main/modules/recipe/ingredients';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { TransportVehicleType } from '@libs/trip-planner-interfaces';
import type { IngredientId } from '@libs/trip-planner-interfaces';

const { t } = i18n.global;

export const useTripOverviewFerryMultipleVehiclesStore = defineStore(
  'TripOverviewFerryMultipleVehiclesStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const paxStore = usePaxStore();

    // State
    const data = ref<FerryMultipleVehiclesData | null>(null);

    // Actions
    function setData(ferryIngredientId: IngredientId): void {
      if (!tripStore.ferryVehicleTypes) {
        return;
      }

      const routes: FerryVehiclesRouteData[] = [];

      recipeStore.ferryOnlyRecipe.forEach((ingredient) => {
        const route: FerryVehiclesRouteData = {
          allowMultipleVehicles: ingredient.allowMultipleVehicles,
          allowVehicles: ingredient.allowVehicles,
          label: `${ingredient.fromPortDisplayText} - ${ingredient.toPortDisplayText}`,
          ingredientId: ingredient.ingredientId,
          selectedVehicle: ingredient.vehicles[0]
            ? ingredient.vehicles[0]
            : TransportVehicleType.None,
          selectedNumberOfVehicles: ingredient.vehicles.length,
          vehiclesLimitation: getVehiclesLimitationMessage(ingredient),
        };

        if (ingredient.ingredientId === ferryIngredientId) {
          routes.unshift(route);
        } else {
          routes.push(route);
        }
      });

      data.value = {
        routes,

        vehicles: tripStore.ferryVehicleTypes,
      };
    }

    function clearData(): void {
      data.value = null;
    }

    function update(data: FerryVehiclesRouteData[]): void {
      data.forEach((route) => {
        recipeStore.getFerryIngredient(route.ingredientId).vehicles =
          Array.from(
            { length: route.selectedNumberOfVehicles },
            () => route.selectedVehicle
          );
      });
      clearData();
    }

    function getVehiclesLimitationMessage(
      ingredient: FerryIngredient
    ): VehiclesLimiation {
      if (!ingredient.allowVehicles) {
        return {
          maxAmount: 0,
          warningMessage: t(
            'planner.ferry_vehicles_limitation.vehicle_change_not_allowed'
          ),
        };
      }

      return {
        maxAmount: paxStore.numberOfAdults,
      };
    }

    return {
      // State
      data,

      // Actions
      clearData,
      setData,
      update,
    };
  }
);
